// Markdown and plain-text output (`TextWriter`, SuiteCompile/TextWriters.swift).
import {
  blockPlainText, isNumbered, listStyleOf, marker, MergeTags, serialize, startsList, type Block, type MergeTagStyle,
} from '@writing-suite/core';
import { blockWithoutMarks } from './pipeline.ts';
import { BOOK_PAGE_KINDS, bylineName, contactName, contentsEntries, documentTitle, heading, isDocument, isPost } from './manuscript.ts';
import type { CompiledManuscript } from './types.ts';

/** A letter's contact block and a document's title lines (synopsis, query letter). */
function documentTop(m: CompiledManuscript, markdown: boolean, year?: number): string[] {
  const out: string[] = [];
  if (m.preset.render.letterhead === true && m.author) out.push([contactName(m.author), ...m.author.contact].join(markdown ? '  \n' : '\n'));
  const lines = documentTitle(m, year);
  if (lines.length) out.push(markdown ? `# ${lines[0]}` : lines[0].toUpperCase());
  out.push(...lines.slice(1));
  return out;
}

const contents = (m: CompiledManuscript) => contentsEntries(m).map((e) => e.text);

export interface TextOptions {
  /** `{year}` in a document's title lines; the Swift takes today's. */
  year?: number;
}

/** `TextWriter.markdown`. */
export function writeMarkdown(m: CompiledManuscript, o: TextOptions = {}): string {
  const r = m.preset.render;
  // A post is headed by its own title (its chapter heading), not the journal's.
  const out = isDocument(m) ? documentTop(m, true, o.year) : isPost(m) ? [] : [`# ${m.title}`];
  if (!isDocument(m) && !isPost(m) && m.author) out.push(`by ${bylineName(m.author)}`);
  for (const section of m.sections) {
    if (section.matter !== null && BOOK_PAGE_KINDS.has(section.matter)) {
      if (section.matter === 'contents') {
        out.push(`## ${section.title}`);
        out.push(contents(m).map((t) => `- ${t}`).join('\n'));
      } else if (section.blocks.length) {
        out.push(markdownBlocks(section.blocks, m.mergeTags));
      }
      continue;
    }
    switch (section.role) {
      case 'part': out.push('# ' + heading(r.partHeading, section).join(': ')); break;
      case 'chapter': out.push((isPost(m) ? '# ' : '## ') + heading(r.chapterHeading, section).join(': ')); break;
      case 'front-matter': case 'back-matter': out.push(`## ${section.title}`); break;
      default: if (section.followsScene) out.push(r.sceneBreak);
    }
    // Merge tags as typed: `[FIRSTNAME]`, not the escaped `\[FIRSTNAME\]` the dialect stores.
    if (section.blocks.length) out.push(markdownBlocks(section.blocks, m.mergeTags));
  }
  if (r.endMarker !== '') out.push(r.endMarker);
  return out.join('\n\n') + '\n';
}

/** `TextWriter.plain`. */
export function writePlainText(m: CompiledManuscript, o: TextOptions = {}): string {
  const r = m.preset.render;
  const out = isDocument(m) ? documentTop(m, false, o.year) : isPost(m) ? [] : [m.title.toUpperCase()];
  if (!isDocument(m) && !isPost(m) && m.author) out.push(`by ${bylineName(m.author)}`);
  for (const section of m.sections) {
    if (section.matter !== null && BOOK_PAGE_KINDS.has(section.matter)) {
      if (section.matter === 'contents') {
        out.push(section.title);
        out.push(contents(m).join('\n'));
      } else {
        out.push(...plainBlocks(section.blocks, r.sceneBreak));
      }
      continue;
    }
    switch (section.role) {
      case 'part': out.push(heading(r.partHeading, section).join('\n').toUpperCase()); break;
      case 'chapter': out.push(heading(r.chapterHeading, section).join('\n')); break;
      case 'front-matter': case 'back-matter': out.push(section.title); break;
      default: if (section.followsScene) out.push(r.sceneBreak);
    }
    out.push(...plainBlocks(section.blocks, r.sceneBreak));
  }
  if (r.endMarker !== '') out.push(r.endMarker);
  return out.join('\n\n') + '\n';
}

/** Prose as Markdown: the dialect, lists as CommonMark writes them (`- `, `1. `; letters and numerals as numbers), merge
 *  tags as typed, without the writer's highlights and colours. */
export function markdownBlocks(blocks: Block[], mergeTags: readonly MergeTagStyle[]): string {
  let out = '';
  let previous: Block | null = null;
  // A list straight after one of its kind takes the other bullet or delimiter, or CommonMark reads the two as one.
  let bullet = '-', delimiter = '.';
  for (const block of blocks.map(blockWithoutMarks)) {
    if (block.kind.type !== 'listItem') {
      if (previous !== null) out += '\n\n';
      out += serialize([block]);
      previous = block;
      continue;
    }
    const { style, number } = block.kind;
    const starts = startsList(block, previous);
    if (starts) {
      const prevStyle = previous ? listStyleOf(previous) : null;
      const follows = prevStyle !== null && isNumbered(prevStyle) === isNumbered(style);
      if (isNumbered(style)) {
        const own = style === 'decimalParen' ? ')' : '.';
        delimiter = follows && delimiter === own ? (own === '.' ? ')' : '.') : own;
      } else {
        bullet = follows && bullet === '-' ? '+' : '-';
      }
    }
    if (previous !== null) out += starts ? '\n\n' : '\n';
    const mark = isNumbered(style) ? `${number}${delimiter}` : bullet;
    const text = serialize([{ kind: { type: 'paragraph' }, inlines: block.inlines, alignment: null }]);
    const indent = ' '.repeat(mark.length + 1);
    out += mark + ' ' + text.replaceAll('\n', '\n' + indent);
    previous = block;
  }
  return MergeTags.verbatim(out, mergeTags);
}

/** Prose as plain text, a paragraph each; a list's items on lines of their own, marked as they're numbered. */
export function plainBlocks(blocks: Block[], sceneBreak: string): string[] {
  const out: string[] = [];
  let previous: Block | null = null;
  for (const block of blocks) {
    switch (block.kind.type) {
      case 'sceneBreak': out.push(sceneBreak); break;
      case 'listItem': {
        const item = marker(block.kind.style, block.kind.number) + ' ' + blockPlainText(block);
        if (!startsList(block, previous) && out.length) out.push(out.pop()! + '\n' + item);
        else out.push(item);
        break;
      }
      default: out.push(blockPlainText(block));
    }
    previous = block;
  }
  return out;
}
